import { useState, useRef, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ImageCropModal from '../../../../components/ImageCropModal/ImageCropModal';

const DEFAULT_COUNTRY_CODE = '91';

const FIELD_LIMITS = {
  leagueName: 80,
  organizerName: 60,
  organizerEmail: 254,
  association: 100,
  city: 60,
  state: 60,
  country: 60,
} as const;

const IMAGE_ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];
const IMAGE_ACCEPT_ATTR = IMAGE_ACCEPT.join(',');
const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const BANNER_MAX_BYTES = 5 * 1024 * 1024;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export interface LeagueDetailsValues {
  leagueId: string | null;
  leagueName: string;
  organizerName: string;
  organizerEmailId: string;
  organizerPhoneNumber: string;
  countryCode: string;
  city: string;
  state: string;
  country: string;
  association: string;
  logoFile: File | null;
  bannerFile: File | null;
}

interface LeagueDetailsStepProps {
  initialValues?: Partial<LeagueDetailsValues>;
  onNext: (data: LeagueDetailsValues) => void | Promise<void>;
}

type FieldKey = keyof typeof FIELD_LIMITS;
type Errors = Partial<Record<FieldKey | 'logo' | 'banner', string>>;

function LeagueDetailsStep({ initialValues, onNext }: LeagueDetailsStepProps) {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [leagueName, setLeagueName] = useState(initialValues?.leagueName ?? '');
  const [organizerName, setOrganizerName] = useState(initialValues?.organizerName ?? '');
  const [organizerPhoneNumber, setOrganizerPhoneNumber] = useState(initialValues?.organizerPhoneNumber ?? '');
  const [organizerEmailId, setOrganizerEmailId] = useState(initialValues?.organizerEmailId ?? '');
  const [city, setCity] = useState(initialValues?.city ?? '');
  const [state, setState] = useState(initialValues?.state ?? '');
  const [country, setCountry] = useState(initialValues?.country ?? '');
  const [association, setAssociation] = useState(initialValues?.association ?? '');
  const [countryCode, setCountryCode] = useState(initialValues?.countryCode ?? DEFAULT_COUNTRY_CODE);
  const [leagueLogoFile, setLeagueLogoFile] = useState<File | null>(initialValues?.logoFile ?? null);
  const [leagueBannerFile, setLeagueBannerFile] = useState<File | null>(initialValues?.bannerFile ?? null);
  const [errors, setErrors] = useState<Errors>({});

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);

  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState('');
  const [cropFor, setCropFor] = useState<'logo' | 'banner'>('logo');
  const [cropMimeType, setCropMimeType] = useState('image/jpeg');
  const [cropFileName, setCropFileName] = useState('image');

  useEffect(() => {
    if (!leagueLogoFile) { setLogoPreview(null); return; }
    const url = URL.createObjectURL(leagueLogoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [leagueLogoFile]);

  useEffect(() => {
    if (!leagueBannerFile) { setBannerPreview(null); return; }
    const url = URL.createObjectURL(leagueBannerFile);
    setBannerPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [leagueBannerFile]);

  const validateImage = (file: File, kind: 'logo' | 'banner'): string | null => {
    if (!IMAGE_ACCEPT.includes(file.type)) {
      return `${kind === 'logo' ? 'Logo' : 'Banner'} must be a JPG, PNG, or WebP image.`;
    }
    const max = kind === 'logo' ? LOGO_MAX_BYTES : BANNER_MAX_BYTES;
    if (file.size > max) {
      return `${kind === 'logo' ? 'Logo' : 'Banner'} must be ${formatBytes(max)} or smaller (selected: ${formatBytes(file.size)}).`;
    }
    return null;
  };

  const openCropModal = (file: File, kind: 'logo' | 'banner') => {
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setCropFor(kind);
      setCropMimeType(file.type || 'image/jpeg');
      setCropFileName(file.name);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    const err = validateImage(file, 'logo');
    if (err) {
      setErrors((p) => ({ ...p, logo: err }));
      if (logoInputRef.current) logoInputRef.current.value = '';
      return;
    }
    setErrors((p) => ({ ...p, logo: undefined }));
    openCropModal(file, 'logo');
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    const err = validateImage(file, 'banner');
    if (err) {
      setErrors((p) => ({ ...p, banner: err }));
      if (bannerInputRef.current) bannerInputRef.current.value = '';
      return;
    }
    setErrors((p) => ({ ...p, banner: undefined }));
    openCropModal(file, 'banner');
    if (bannerInputRef.current) bannerInputRef.current.value = '';
  };

  const handleCropConfirm = (croppedFile: File) => {
    setCropOpen(false);
    if (cropFor === 'logo') {
      setLeagueLogoFile(croppedFile);
    } else {
      setLeagueBannerFile(croppedFile);
    }
  };

  const validate = (): Errors => {
    const e: Errors = {};
    const required: Array<[FieldKey, string, string]> = [
      ['leagueName', leagueName, 'League Name'],
      ['organizerName', organizerName, 'Organiser Name'],
      ['organizerEmail', organizerEmailId, 'Organiser Email'],
      ['association', association, 'Association'],
      ['city', city, 'City'],
      ['state', state, 'State'],
      ['country', country, 'Country'],
    ];
    for (const [key, value, label] of required) {
      const v = value.trim();
      if (!v) {
        e[key] = `${label} is required.`;
        continue;
      }
      if (v.length > FIELD_LIMITS[key]) {
        e[key] = `${label} must be at most ${FIELD_LIMITS[key]} characters.`;
      }
    }
    if (!e.organizerEmail && !EMAIL_RE.test(organizerEmailId.trim())) {
      e.organizerEmail = 'Enter a valid email address.';
    }
    if (!leagueLogoFile) e.logo = 'Please upload a league logo.';
    if (!leagueBannerFile) e.banner = 'Please upload a league banner.';
    return e;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;

    await onNext({
      leagueId: initialValues?.leagueId ?? null,
      leagueName: leagueName.trim(),
      organizerName: organizerName.trim(),
      organizerEmailId: organizerEmailId.trim(),
      organizerPhoneNumber: organizerPhoneNumber,
      countryCode,
      city: city.trim(),
      state: state.trim(),
      country: country.trim(),
      association: association.trim(),
      logoFile: leagueLogoFile,
      bannerFile: leagueBannerFile,
    });
  };

  const counter = (key: FieldKey, value: string) => {
    const max = FIELD_LIMITS[key];
    if (value.length < max - 10) return null;
    return <span className="create-league-char-counter">{value.length}/{max}</span>;
  };

  const fieldError = (key: keyof Errors) =>
    errors[key] ? <span className="create-league-field-error" role="alert">{errors[key]}</span> : null;

  return (
    <>
    <ImageCropModal
      open={cropOpen}
      imageSrc={cropSrc}
      aspect={cropFor === 'logo' ? 1 : 16 / 9}
      mimeType={cropMimeType}
      fileName={cropFileName}
      onCrop={handleCropConfirm}
      onCancel={() => setCropOpen(false)}
    />
    <form onSubmit={handleSubmit} className="create-league-step-form league-details-step" noValidate>
      <h2 className="create-league-step-title">League Details</h2>
      <div className="league-details-grid">
        <div className="league-details-col">
          <div className="create-league-field">
            <label>League Logo <span className="req">*</span></label>
            <div className="league-upload-row">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="League logo preview"
                  className="league-logo-preview"
                />
              ) : (
                <div className="league-logo-preview league-logo-preview--empty" aria-hidden />
              )}
            <div className="league-logo-input-wrap" style={{ flex: 1 }}>
              <input
                type="text"
                placeholder="Logo image"
                value={leagueLogoFile?.name ?? ''}
                readOnly
                className="league-logo-text"
              />
              <input
                ref={logoInputRef}
                type="file"
                accept={IMAGE_ACCEPT_ATTR}
                className="league-logo-file"
                onChange={handleLogoChange}
                data-testid="logo-file-input"
              />
              <button
                type="button"
                className="league-logo-upload-btn"
                onClick={() => logoInputRef.current?.click()}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                >
                  <path
                    d="M12 4l-6 6h4v8h4v-8h4l-6-6z"
                    fill="currentColor"
                  />
                </svg>
                Upload
              </button>
            </div>
            </div>
            <span className="create-league-field-hint">
              JPG, PNG, or WebP. Max {formatBytes(LOGO_MAX_BYTES)}.
            </span>
            {fieldError('logo')}
          </div>
          <div className="create-league-field">
            <label>League Banner <span className="req">*</span></label>
            <div className="league-logo-input-wrap">
              <input
                type="text"
                placeholder="Banner image"
                value={leagueBannerFile?.name ?? ''}
                readOnly
                className="league-logo-text"
              />
              <input
                ref={bannerInputRef}
                type="file"
                accept={IMAGE_ACCEPT_ATTR}
                className="league-logo-file"
                onChange={handleBannerChange}
                data-testid="banner-file-input"
              />
              <button
                type="button"
                className="league-logo-upload-btn"
                onClick={() => bannerInputRef.current?.click()}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                >
                  <path
                    d="M12 4l-6 6h4v8h4v-8h4l-6-6z"
                    fill="currentColor"
                  />
                </svg>
                Upload
              </button>
            </div>
            <span className="create-league-field-hint">
              JPG, PNG, or WebP. Max {formatBytes(BANNER_MAX_BYTES)}.
            </span>
            {fieldError('banner')}
            {bannerPreview && (
              <div className="league-banner-preview">
                <img src={bannerPreview} alt="League banner preview" />
              </div>
            )}
          </div>
          <div className="create-league-field">
            <label>Organiser Name <span className="req">*</span></label>
            <input
              type="text"
              placeholder="Enter Organiser Name"
              value={organizerName}
              maxLength={FIELD_LIMITS.organizerName}
              onChange={(e) => setOrganizerName(e.target.value)}
            />
            {counter('organizerName', organizerName)}
            {fieldError('organizerName')}
          </div>
          <div className="form-control-wrap create-league-phone-field">
            <div className="form-group">
              <label>
                Mobile Number <span className="req">*</span>
              </label>
              <div className="phone">
                <PhoneInput
                  country="in"
                  value={organizerPhoneNumber}
                  countryCodeEditable={false}
                  onChange={(val, country) => {
                    setOrganizerPhoneNumber(val);
                    if (country && 'dialCode' in country && country.dialCode) {
                      setCountryCode(String(country.dialCode));
                    }
                  }}
                  inputClass="form-control phone-input-field"
                  containerClass="phone-input-container"
                  placeholder="Enter contact number"
                  enableSearch
                />
              </div>
            </div>
          </div>
        </div>
        <div className="league-details-col">
          <div className="create-league-field">
            <label>League Name <span className="req">*</span></label>
            <input
              type="text"
              placeholder="Enter League Name"
              value={leagueName}
              maxLength={FIELD_LIMITS.leagueName}
              onChange={(e) => setLeagueName(e.target.value)}
              data-testid="league-name-input"
            />
            {counter('leagueName', leagueName)}
            {fieldError('leagueName')}
          </div>
          <div className="create-league-field">
            <label>Organiser Email <span className="req">*</span></label>
            <input
              type="email"
              placeholder="Enter Email Id"
              value={organizerEmailId}
              maxLength={FIELD_LIMITS.organizerEmail}
              onChange={(e) => setOrganizerEmailId(e.target.value)}
            />
            {counter('organizerEmail', organizerEmailId)}
            {fieldError('organizerEmail')}
          </div>
          <div className="create-league-field">
            <label>Association <span className="req">*</span></label>
            <input
              type="text"
              placeholder="Enter Association"
              value={association}
              maxLength={FIELD_LIMITS.association}
              onChange={(e) => setAssociation(e.target.value)}
            />
            {counter('association', association)}
            {fieldError('association')}
          </div>
        </div>
      </div>
      <div className="league-details-address-row">
        <div className="create-league-field">
          <label>City <span className="req">*</span></label>
          <input
            type="text"
            placeholder="Enter city"
            value={city}
            maxLength={FIELD_LIMITS.city}
            onChange={(e) => setCity(e.target.value)}
          />
          {counter('city', city)}
          {fieldError('city')}
        </div>
        <div className="create-league-field">
          <label>State <span className="req">*</span></label>
          <input
            type="text"
            placeholder="Enter State"
            value={state}
            maxLength={FIELD_LIMITS.state}
            onChange={(e) => setState(e.target.value)}
          />
          {counter('state', state)}
          {fieldError('state')}
        </div>
        <div className="create-league-field">
          <label>Country <span className="req">*</span></label>
          <input
            type="text"
            placeholder="Enter Country"
            value={country}
            maxLength={FIELD_LIMITS.country}
            onChange={(e) => setCountry(e.target.value)}
          />
          {counter('country', country)}
          {fieldError('country')}
        </div>
      </div>
      <div className="create-league-step-actions">
        <button type="submit" className="create-league-btn-primary">
          NEXT
        </button>
      </div>
    </form>
    </>
  );
}

export default LeagueDetailsStep;
